import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import { CHANNELS } from '../../../constants/channels';
import { CymbioSpinner } from '../../common/Spinner';
import Select from '../../common/Select';
import {
  tiktokListBrands,
  tiktokListProductDesigners,
  tiktokPostBrandIdMapping,
} from '../../../store/actions/channels';
import { TikTokNoBrands } from './TikTokNoBrands';

import styles from './TikTokBrandIdMapper.module.scss';

export function TikTokBrandIdMapper(props) {
  const { supplierId, retailerId, onSkip, onContinue, currentMapping, resetOnSkip = true } = props;
  const dispatch = useDispatch();
  const {
    data: {
      [CHANNELS.TIKTOK]: { brands, designers },
    },
    loading: { listBrands: loadingBrands, listDesigners: loadingDesigners, postBrandIdMapping: loadingPostMapping },
  } = useSelector((state) => state.channels);

  const [mapping, setMapping] = useState(currentMapping || {});
  const brandOptions = useMemo(() => {
    if (!brands?.length) return [];

    const options = brands.map((brand) => ({
      label: brand.name,
      value: brand.id,
    }));
    return options;
  }, [brands]);

  const loading = loadingBrands || loadingDesigners;
  const submitBrandMapping = () => {
    dispatch(tiktokPostBrandIdMapping({ supplierId, retailerId, mapping }));
    onContinue?.();
  };

  const skipBrandMapping = () => {
    if (resetOnSkip) {
      dispatch(tiktokPostBrandIdMapping({ supplierId, retailerId, mapping: {} }));
    }
    onSkip?.();
  };

  const refreshTikTokBrands = () => {
    dispatch(tiktokListBrands({ supplierId, retailerId }));
  };

  useEffect(() => {
    dispatch(tiktokListBrands({ supplierId, retailerId }));
    dispatch(tiktokListProductDesigners({ supplierId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, retailerId]);

  useEffect(
    function setInitialDesigners() {
      if (!isEmpty(currentMapping) || !designers?.length || !brands?.length || loadingPostMapping) return;
      const initialMapping = designers.reduce((allDesigners, designer) => {
        allDesigners[designer] = brands[0].id.toString();
        return allDesigners;
      }, {});

      setMapping(initialMapping);
    },
    [designers, brands, currentMapping, loadingPostMapping],
  );

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <h3>Getting brands from TikTok and designers from Cymbio...</h3>
        <CymbioSpinner />
      </div>
    );
  }

  if (brands.length === 0) {
    return <TikTokNoBrands onRefresh={refreshTikTokBrands} onSkip={skipBrandMapping} />;
  }

  return (
    <div className={styles.wrapper}>
      <h3>Please choose a TTS brand for each Cymbio designer:</h3>
      <div className={styles.selectorWrapper}>
        {designers.map((designer) => (
          <div className={styles.designerRow} key={designer}>
            <div className={styles.designerName}>{designer}</div>
            <Select
              options={brandOptions}
              onChange={(value) => setMapping({ ...mapping, [designer]: value })}
              value={mapping[designer]}
            />
          </div>
        ))}
      </div>
      <div className={styles.buttons}>
        <button onClick={submitBrandMapping} disabled={loading}>
          Continue
        </button>
        <button onClick={skipBrandMapping}>Skip</button>
      </div>
    </div>
  );
}

TikTokBrandIdMapper.propTypes = {
  supplierId: PropTypes.number.isRequired,
  retailerId: PropTypes.number.isRequired,
  resetOnSkip: PropTypes.bool,
  onSkip: PropTypes.func,
  onContinue: PropTypes.func,
  currentMapping: PropTypes.object,
};
